import { CULTIV_LOCALES } from '../site';
import { BLOG_POSTS } from './posts';
import type { BlogBlock, BlogLocale, BlogPost, BlogTranslation } from './types';

/** Language the posts are authored in — the fallback when a translation is missing. */
export const BLOG_SOURCE_LOCALE: BlogLocale = 'nl';

const WORDS_PER_MINUTE = 200;

export function findPost(slug: string, posts: BlogPost[] = BLOG_POSTS): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}

/** Locales this post is actually readable in, in CULTIV_LOCALES order. */
export function translatedLocales(post: BlogPost): BlogLocale[] {
  return CULTIV_LOCALES.filter((l) => Boolean(post.i18n[l]));
}

/** The requested translation, falling back to the source locale and then to any
 *  available one, so a direct URL in an untranslated locale still renders. */
export function getTranslation(post: BlogPost, locale: string): BlogTranslation {
  const exact = post.i18n[locale as BlogLocale];
  if (exact) return exact;
  const source = post.i18n[BLOG_SOURCE_LOCALE];
  if (source) return source;
  const any = translatedLocales(post)[0];
  return post.i18n[any] as BlogTranslation;
}

/** Posts readable in this locale, newest first. */
export function postsForLocale(locale: string, posts: BlogPost[] = BLOG_POSTS): BlogPost[] {
  return posts
    .filter((p) => Boolean(p.i18n[locale as BlogLocale]))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

export function readingMinutes(body: BlogBlock[]): number {
  const words = body.reduce((n, block) => {
    if (block.t === 'img') return n;
    const text = Array.isArray(block.v) ? block.v.join(' ') : block.v;
    return n + text.trim().split(/\s+/).filter(Boolean).length;
  }, 0);
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
}

/** Regional tags, not bare language codes: bare `en` formats US-first (July 29, 2026).
 *  Mirrors the booking page's mapping so dates read the same across the site. */
export function formatPostDate(iso: string, locale: string): string {
  const tag = locale === 'en' ? 'en-GB' : `${locale}-BE`;
  return new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(`${iso}T00:00:00Z`)
  );
}
